import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import type { Site } from "@sitebrew/contracts/v1";
import { useArchiveSite, useCreateSite, useRenameSite } from "@/lib/sites";
import { Button } from "@/components/ui/button";
import { BusyButton, ButtonNav } from "@/components/button";
import { usePageFrame } from "@/components/layout";
import { SiteStatus } from "@/screens/sites";
import { LoginCard, LoginCardSubtitle, LoginCardTitle, Notice } from "@/components/ui/login-card";
import { ResourcePageLayout } from "@/components/resource-page";
import { FieldError, Input, Label } from "@/components/ui/field";
import { SiteActivity } from "@/screens/site-activity";

/**
 * Renaming, in place of the title.
 *
 * The **only** editable thing about a site (design note §11): the address is a
 * hostname and a hostname that has been public is never reissued, so there is
 * no field for it — and the API refuses a request that carries one, which means
 * a control offering it would be a control whose every use is an error.
 *
 * It opens where the name already is, rather than on a separate screen. What is
 * being changed is one string somebody is looking at, and a screen in between
 * would ask them to find it again.
 */
function RenameForm({ site, onDone }: { site: Site; onDone: () => void }) {
  const rename = useRenameSite(site);
  const [name, setName] = useState(site.name);

  const trimmed = name.trim();
  const failure = rename.error instanceof ApiError ? rename.error : null;

  return (
    <form
      className="mb-[1.2rem]"
      noValidate
      onSubmit={async (event) => {
        // Never the default submit: a form whose only control is `type=submit`
        // still reloads the document when Enter reaches it, which throws the
        // session cache away to save a string.
        event.preventDefault();
        if (!trimmed || trimmed === site.name) return onDone();
        const renamed = await rename.mutateAsync(trimmed).catch(() => null);
        if (renamed) onDone();
      }}
    >
      <Label htmlFor="site-name">Name</Label>
      <Input
        id="site-name"
        autoFocus
        autoComplete="off"
        value={name}
        aria-invalid={failure ? true : undefined}
        onChange={(event) => setName(event.target.value)}
      />
      {/*
        The API's own sentence again. It is the side that knows the bound, so a
        second copy of "1–200 characters" here would be a number to keep in step
        across two repositories.
      */}
      <FieldError>{failure?.message}</FieldError>

      <BusyButton className="mt-[.9rem] w-full" type="submit" busy={rename.isPending} busyLabel="Saving…" disabled={!trimmed}>
        Save name
      </BusyButton>
      <Button className="mt-[.9rem] w-full" type="button" variant="outline" onClick={onDone}>
        Cancel
      </Button>
    </form>
  );
}

/** One fact about a site, as a row. */
function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    /*
      Wraps rather than truncates: `truncate` on a right-aligned value hides
      the end of a hostname, and the end is the part that differs. A 63
      character slug makes a 76 character address, which does not fit a phone's
      width on one line at all — so it breaks, and breaks anywhere, since a
      break at the hyphen would read as two addresses (review of #10).
    */
    <div className="mb-[.6rem] flex items-baseline justify-between gap-[.8rem]">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right break-all">{children}</span>
    </div>
  );
}

/**
 * A site: what it is, and the one thing that can be done to it.
 *
 * The hostname is shown as text and not as a link. It is reserved the moment
 * the site is registered, but M2 serves nothing at it — a link would be a
 * promise the platform does not keep yet, which is the same mistake as a
 * "Continue with Google" button that goes nowhere (`sitebrew-console#8`). The
 * repository *is* a link, because it exists and opening it is useful.
 */
export function SiteScreen({ site }: { site: Site }) {
  /*
   * What the address cannot say. The route knows this is a site's page; only
   * this knows which site — and which workspace it is in, since the URL carries
   * no workspace here. Both go through the one channel the frame reads, so
   * there is never a second way to tell it something.
   */
  usePageFrame({ title: site.name, workspaceId: site.workspaceId });

  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const archive = useArchiveSite(site);
  const resume = useCreateSite(site.workspaceId);

  const failure = archive.error instanceof ApiError ? archive.error : null;
  const resumeFailure = resume.error instanceof ApiError ? resume.error : null;

  return (
    // No subtitle: the fleet's `ResourcePageLayout` is a title and an action.
    // The slug used to sit under the name here, and it is a line down in
    // `Address` — as the hostname it becomes, which is the form worth reading.
    <ResourcePageLayout title={site.name}>
      {site.status === "creating" ? (
        /*
         * An unfinished site is not a broken one: the row is registered and the
         * repository is not there yet, and §9 makes creating it again the way
         * to finish it — the repository name is a function of the site's id, so
         * a resume targets the same repository rather than making a second one.
         * Saying so beats leaving somebody with a site that says "Setting up"
         * forever and no control that does anything about it.
         */
        <Notice className="mb-5">
          This site is registered but its repository was not finished. Resuming picks up where it
          stopped.
        </Notice>
      ) : null}

      {resumeFailure ? <Notice className="mb-5">{resumeFailure.message}</Notice> : null}
      {failure ? <Notice className="mb-5">{failure.message}</Notice> : null}

      {/*
        Deliberately *not* keyed on the name.

        It used to be `key={site.name}`, to make a second rename open on the
        current name rather than the first one. That was a guard against
        nothing: the form is unmounted the moment it is done, so reopening it
        seeds `useState` from the site as it is by then either way — and a test
        that renames twice passes with the key or without it (DevGuru's review
        of #10 asked for exactly that test, which is how this surfaced).

        What the key did do is fire in the one case it was never meant for. A
        background refetch that brings back a new name — the site renamed in
        another tab — would change the key while the form is open and remount
        it, throwing away what somebody is in the middle of typing. So the key
        is gone and that case is the test.
      */}
      {renaming ? <RenameForm site={site} onDone={() => setRenaming(false)} /> : null}

      <div className="mb-[1.2rem] rounded-lg border border-border p-[.8rem]">
        <Detail label="Address">
          {site.hostname ? (
            <code className="text-sm">{site.hostname}</code>
          ) : (
            <span className="text-sm text-muted-foreground">assigned when the site is registered</span>
          )}
        </Detail>
        <Detail label="Repository">
          {site.repo ? (
            <a
              className="text-sm text-primary"
              href={`https://github.com/${site.repo}`}
              target="_blank"
              rel="noreferrer"
            >
              {site.repo}
            </a>
          ) : (
            <span className="text-sm text-muted-foreground">not created yet</span>
          )}
        </Detail>
        <Detail label="Status">
          {/*
            The same badge the list draws, from the same table. These words
            were spelled out again here, which is the shape a pair of lists
            always has: both look right, and the day one of them changes only
            one screen says so.
          */}
          <SiteStatus status={site.status} />
        </Detail>
        <Detail label="Created">
          <span className="text-sm">
            {new Date(site.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
          </span>
        </Detail>
      </div>

      {/*
        The actions in a row rather than stacked full width: that shape came
        from a card 480px across, and in a page it reads as three of the same
        thing rather than as three different ones.
      */}
      <div className="flex flex-wrap items-center gap-2">
      {site.status === "creating" ? (
        <BusyButton
          busy={resume.isPending}
          busyLabel="Finishing…"
          onClick={() => resume.mutate({ name: site.name, slug: site.slug })}
        >
          Finish setting up
        </BusyButton>
      ) : null}

      {!renaming && !confirming ? (
        <Button variant="outline" onClick={() => setRenaming(true)}>
          Rename
        </Button>
      ) : null}

      {confirming ? null : (
        <Button variant="outline" onClick={() => setConfirming(true)}>
          Delete this site
        </Button>
      )}
      </div>

      {confirming ? (
        <>
          {/*
            What deleting actually does, before it is done and not after.
            Archiving is the decision (2026-08-16): nothing is erased, and the
            address is spent — a hostname that has been public is never
            reissued, not even back to this workspace. Somebody who expects to
            re-create the site under the same address tomorrow has to learn
            that here, while the button still says "Cancel".
          */}
          <Notice className="mb-[.9rem]">
            {/*
              The address is on its own line rather than inside the sentence.
              Broken at a hyphen mid-sentence it read as two things — `acme-`
              and `bakery.sitebrew.app` — and holding it together with
              `whitespace-nowrap` traded that for text leaving the card, since a
              slug may be a whole DNS label: 63 characters, so 76 with the
              domain, where a phone fits about 30. On a line of its own a break
              is unambiguous, because there is nothing else on those lines
              (DevGuru's review of #10).
            */}
            Deleting archives this site. Its repository is archived on GitHub — nothing is erased —
            and this address is never reissued, not even to this workspace:
            {site.hostname ? (
              <code className="mt-[.5rem] block break-all">{site.hostname}</code>
            ) : (
              <span className="mt-[.5rem] block">the one it holds</span>
            )}
          </Notice>
          <div className="flex flex-wrap items-center gap-2">
          <BusyButton
            busy={archive.isPending}
            busyLabel="Archiving…"
            onClick={async () => {
              const gone = await archive
                .mutateAsync()
                .then(() => true)
                .catch((err) => err instanceof ApiError && err.status === 404);

              // `404` is "not yours, or already archived" — from here the
              // second is what it means, so the answer is the same as success.
              if (gone) {
                await navigate({
                  to: "/workspaces/$workspaceId/sites",
                  params: { workspaceId: site.workspaceId },
                });
              }
            }}
          >
            Archive this site
          </BusyButton>
          <Button variant="outline" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          </div>
        </>
      ) : null}

      <SiteActivity siteId={site.id} />
    </ResourcePageLayout>
  );
}

/**
 * A site that is not there — archived, or never this account's.
 *
 * One screen for both, because the API gives one answer for both on purpose:
 * telling them apart would confirm that somebody else's site exists (§6).
 */
export function NoSuchSiteScreen() {
  return (
    <LoginCard>
      <LoginCardTitle>No such site</LoginCardTitle>
      <LoginCardSubtitle>It has been archived, or it belongs to another workspace.</LoginCardSubtitle>
      <ButtonNav className="mt-[.9rem] w-full" variant="outline" to="/">
        ← Account
      </ButtonNav>
    </LoginCard>
  );
}
